/**
 * HOW NUMBERS, MONEY AND DATES READ ON A CTS SCREEN.
 *
 * Money is integer paise everywhere in this system (D123's plain-English rule
 * has a twin in the code: one representation, converted at the edges). It
 * becomes rupees HERE and nowhere else, grouped the Indian way — ₹12,34,567.00
 * — because that is how the office reads a figure, and a lakh written as
 * 1,234,567 is read twice before it is understood.
 *
 * Nothing in this file decides anything. It formats what the server said.
 */

const RUPEE = '₹';

/** Indian digit grouping: the last three, then twos. */
function groupIndian(whole: string): string {
  if (whole.length <= 3) return whole;
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/** Paise as rupees, always two decimals, always grouped. `-` for nothing at all. */
export function money(paise: number | null | undefined): string {
  if (paise === null || paise === undefined || Number.isNaN(paise)) return '—';
  const negative = paise < 0;
  const abs = Math.abs(Math.round(paise));
  const whole = Math.floor(abs / 100).toString();
  const fraction = (abs % 100).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${RUPEE}${groupIndian(whole)}.${fraction}`;
}

/** A plain count, grouped the same way. */
export function count(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const negative = n < 0;
  return `${negative ? '-' : ''}${groupIndian(Math.abs(Math.round(n)).toString())}`;
}

/**
 * A QUANTITY IN ITS OWN UNIT (0164). An order is owed in gas — "100 Nm³", "12.5
 * kg" — and in cylinders only when that is genuinely what was ordered. Gas
 * carries up to three decimals and drops the trailing zeros, because 6.6 Nm³
 * and 6.600 Nm³ are the same figure and only one of them reads like a number.
 */
export function qty(n: number | null | undefined, unit: string | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (unit === 'cylinder') return `${count(n)} cylinder${Math.round(n) === 1 ? '' : 's'}`;
  if (unit === null || unit === undefined) return count(n);
  // groupIndian takes digits only, so the fraction is kept out of its way
  const negative = n < 0;
  const trimmed = Number(Math.abs(n).toFixed(3)).toString();
  const [whole, fraction] = trimmed.split('.');
  const grouped = groupIndian(whole ?? '0') + (fraction ? `.${fraction}` : '');
  return `${negative ? '-' : ''}${grouped} ${unit === 'm3' ? 'Nm³' : unit}`;
}

/** The same figure for a mixed order: "100 Nm³ + 25 kg", in a stable order. */
export function qtyByUnit(by: Record<string, number> | null | undefined): string {
  const parts = Object.entries(by ?? {}).filter(([, v]) => v > 0)
    .sort(([a], [b]) => a.localeCompare(b)).map(([u, v]) => qty(v, u));
  return parts.length ? parts.join(' + ') : '—';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A business date (YYYY-MM-DD) as `07 Jun 2026`. Never re-parsed through a timezone. */
export function date(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

/** An instant, in the reader's own timezone, as `07 Jun 2026, 4:43 pm`. */
export function instant(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  let hours = d.getHours();
  const meridiem = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${date(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)}, ${hours}:${minutes} ${meridiem}`;
}

/** Today, as the business writes it. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `Wednesday, 10 June 2026` — the line under a screen's greeting. */
export function longDate(d: Date = new Date()): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Morning, afternoon or evening — the greeting CTS 1 opens with. */
export function greeting(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** `3 days`, `1 day`, `—`. */
export function days(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${count(n)} ${Math.abs(n) === 1 ? 'day' : 'days'}`;
}

/** Rupees typed by a person into the paise the commands take. */
export function rupeesToPaise(typed: string): number | null {
  const cleaned = typed.replace(/[,\s₹]/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

/** A status word as the screen shows it: `load_approved` → `load approved`. */
export function words(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace(/_/g, ' ');
}

/** "22 Sep 2026, 12:23 am" from a database timestamp — never the microseconds. */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (!m) return date(iso);
  const h = Number(m[4]); const ampm = h < 12 ? 'am' : 'pm'; const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${date(iso)}, ${h12}:${m[5]} ${ampm}`;
}
